import { useTranslation } from 'react-i18next'

import { DatePicker } from '#/shared/ui/date-picker'
import { TimePicker } from '#/shared/ui/time-picker'

import { CAMPAIGN_STARTS } from '../model/use-campaign-composer'
import { CampaignChoiceChip } from './campaign-choice-chip'
import type {
  CampaignSendRate,
  CampaignStart,
  UseCampaignComposerResult,
} from '../model/use-campaign-composer'

const START_ORDER: readonly CampaignStart[] = [
  CAMPAIGN_STARTS.now,
  CAMPAIGN_STARTS.schedule,
]

interface CampaignComposerControlsProps {
  composer: UseCampaignComposerResult
}

/** Send rate and start — identical on both channels, only the rates differ. */
export function CampaignComposerControls({
  composer,
}: CampaignComposerControlsProps) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-2">
      {/* Chips, like the wait row above — one control language for every
          choice on the step. */}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs text-warm-500">
          {t('campaigns.composer.sendRate')}
        </span>
        {composer.rates.map((rate: CampaignSendRate) => (
          <CampaignChoiceChip
            key={rate}
            label={t(`campaigns.composer.rate.${rate}`)}
            selected={composer.rate === rate}
            onClick={() => composer.setRate(rate)}
          />
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs text-warm-500">
          {t('campaigns.composer.start')}
        </span>
        {START_ORDER.map((start) => (
          <CampaignChoiceChip
            key={start}
            label={t(`campaigns.composer.startOption.${start}`)}
            selected={composer.start === start}
            onClick={() => composer.setStart(start)}
          />
        ))}
      </div>
      {/* Disclosed by the Schedule chip, so it reads as subordinate to it:
          its own line under the chips rather than trailing off to the right,
          where the two fields wrapped against them. */}
      {composer.start === CAMPAIGN_STARTS.schedule && (
        <ScheduleFields composer={composer} />
      )}
    </div>
  )
}

/**
 * When the step is held until. Both controls move as one row rather than
 * wrapping apart.
 *
 * The delay still applies — this holds the one step without holding the
 * campaign — so the note names which of the two wins.
 */
function ScheduleFields({ composer }: CampaignComposerControlsProps) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-center gap-2">
        {/* The picker's own root is `w-full`, so the width is set on a box
            around it — left on the component it only reaches the trigger, and
            the full-width root both pushes the time field onto its own line
            and makes the default centre alignment hang the calendar off to
            the right of a field it is supposed to sit under. */}
        <div className="w-40 shrink-0">
          <DatePicker
            instantApply
            align="left"
            value={composer.startDate}
            onValueChange={composer.setStartDate}
            fromDate={new Date()}
            dateFormat="MMM D, YYYY"
            placeholder={t('campaigns.composer.scheduleDate')}
          />
        </div>
        <TimePicker
          portal
          size="sm"
          value={composer.startTime}
          onChange={composer.setStartTime}
          placeholder={t('campaigns.composer.scheduleTime')}
          className="w-28 shrink-0"
        />
      </div>
      <p className="text-xs text-warm-500">
        {t('campaigns.composer.scheduleNote')}
      </p>
    </div>
  )
}
